package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * How much an agent may do without asking.
 *
 * **No level ever overwrites APPROVED.** That is not a setting: approval is a human's word, and an
 * autonomy level that could erase one would make the whole review lane advisory.
 */
@Serializable
enum class AgentAutonomy(val id: String, val description: String) {
    /** Everything queues for a person to accept. The default, and the one most fleets should keep. */
    PROPOSE("propose", "Everything it writes waits for somebody to accept it"),

    /** May fill strings nobody has translated. **May never overwrite existing text.** */
    WRITE_EMPTY("write_empty", "May fill empty strings directly, never overwrite one"),

    /** May overwrite TRANSLATED text. Still never APPROVED. */
    WRITE("write", "May overwrite translations that are not approved"),
}

/**
 * An agent's credential, as the dashboard shows it.
 *
 * The token appears once, at creation. Everything else here is what somebody needs to answer "should
 * this still exist" six weeks later.
 */
@Serializable
data class AgentConnectionDto(
    val id: String,
    val name: String,
    val scopes: List<TokenScope> = emptyList(),
    val projectSlugs: List<String> = emptyList(),
    val autonomy: AgentAutonomy = AgentAutonomy.PROPOSE,
    /** Zero means it cannot spend at all, which is the default and usually the right answer. */
    val creditBudgetMonthly: Int = 0,
    val creditBudgetDaily: Int = 0,
    val maxCreditsPerCall: Int = 0,
    val paused: Boolean = false,
    /**
     * When it stops working, and there is no "never".
     *
     * A credential with no end date outlives the reason it was created — the pipeline is
     * decommissioned, the contractor leaves — and keeps working because nothing ever asked it to
     * stop. Renewing is one click by somebody who is still here, which is the check being bought.
     */
    val expiresAt: String,
    /** Self-reported by the MCP client. **Untrusted text**: escaped, never markup, never a template. */
    val clientName: String? = null,
    val createdAt: String,
    val lastUsedAt: String? = null,
    /** Shown once, at creation, and never again. `lgm_…`. */
    val token: String? = null,
    /**
     * The role this connection borrows from, right now.
     *
     * Shown because it is the thing most likely to have changed underneath somebody: an agent set up
     * by an admin who has since become a translator can no longer approve, and the page should say
     * so before somebody spends an afternoon debugging it.
     */
    val grantingRole: Role? = null,
)

@Serializable
data class CreateAgentConnectionRequest(
    val name: String,
    val scopes: List<TokenScope> = emptyList(),
    val projectSlugs: List<String> = emptyList(),
    val autonomy: AgentAutonomy = AgentAutonomy.PROPOSE,
    val creditBudgetMonthly: Int = 0,
    val creditBudgetDaily: Int = 0,
    val maxCreditsPerCall: Int = 0,
    /** Thirty by default, ninety at most. A request for more is clamped rather than refused. */
    val expiresInDays: Int = 30,
    val clientName: String? = null,
)

/** The numbers, and only the numbers. Widening what an agent may *reach* re-mints instead. */
@Serializable
data class UpdateAgentLimitsRequest(
    val creditBudgetMonthly: Int,
    val creditBudgetDaily: Int,
    val maxCreditsPerCall: Int,
    /** Set to renew. Null leaves the expiry where it is. */
    val renewForDays: Int? = null,
)

/**
 * Why a call was refused.
 *
 * Three of these are distinguishable on purpose: "ask for a wider token", "ask an admin" and "you
 * are looking at the wrong project" are three different fixes, and one generic `forbidden` sends an
 * agent into a retry loop against all three.
 */
@Serializable
enum class AgentDenial(val id: String) {
    SCOPE_MISSING("scope_missing"),
    ROLE_INSUFFICIENT("role_insufficient"),
    PROJECT_NOT_IN_SCOPE("project_not_in_scope"),
    CONNECTION_PAUSED("connection_paused"),
    CONNECTION_REVOKED("connection_revoked"),
    CONNECTION_EXPIRED("connection_expired"),
    BUDGET_EXHAUSTED("budget_exhausted"),
    RATE_LIMITED("rate_limited"),
}

/**
 * One tool, one day, allowed and refused side by side.
 *
 * **The refusals are the point.** "propose_translation — 0 allowed, 412 denied" turns a silently
 * failing agent into a one-line diagnosis; a log of successes alone cannot answer why nothing
 * happened.
 */
@Serializable
data class AgentLogRowDto(
    val tool: String,
    /** A UTC date, `yyyy-mm-dd`. Agents run at all hours and the budget windows are UTC. */
    val day: String,
    val calls: Int,
    val denied: Int,
    val credits: Int,
)

@Serializable
data class AgentLogDto(val rows: List<AgentLogRowDto> = emptyList())

/** The new credential, shown once. Rotating does not change what the connection may do. */
@Serializable
data class RotatedAgentTokenDto(val token: String)

/** How many were actually stopped. Zero is a real answer: everything was already paused. */
@Serializable
data class PausedAllDto(val paused: Int)
